import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import axios from "axios";
import { DragDropContext } from "@hello-pangea/dnd";
import OrdersColumn from "./OrdersColumn";
import Pusher from "pusher-js";
import "./OrdersTab.css";

const ORDER_STATUSES = [
  { id: "todo", label: "order received" },
  { id: "inprogress", label: "processing" },
  { id: "completed", label: "completed" },
];

// ✅ Move maps OUTSIDE component so they are stable (fixes ESLint deps)
const STATUS_MAP = {
  "to do": "todo",
  todo: "todo",
  "in progress": "inprogress",
  processing: "inprogress",
  completed: "completed",
};

const DB_STATUS_MAP = {
  todo: "to do",
  inprogress: "in progress",
  completed: "completed",
};

export default function OrdersTab({ setIsAnyModalActive }) {
  const [columns, setColumns] = useState({
    todo: [],
    inprogress: [],
    completed: [],
  });
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dragLoading, setDragLoading] = useState(false);
  const [pusherError, setPusherError] = useState(false);

  const pollingInterval = useRef(null);

  const sounds = useRef({
    todo: new Audio("/sounds/notification.mp3"),
    inprogress: new Audio("/sounds/inprogress-order.mp3"),
    completed: new Audio("/sounds/completed-order.mp3"),
  });

  const sortOrders = useCallback((orders, columnId) => {
    if (columnId === "completed") {
      return [...orders].sort(
        (a, b) => new Date(b.completed_at) - new Date(a.completed_at)
      );
    }
    return [...orders].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );
  }, []);

  const normalizeColumns = useCallback((orders) => {
    const col = { todo: [], inprogress: [], completed: [] };

    (orders || []).forEach((o) => {
      const key = STATUS_MAP[String(o?.status || "").trim().toLowerCase()];
      if (key) col[key].push(o);
    });

    return col;
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      const res = await axios.get(`https://admin.deedelicious.com/php/get-orders.php`);
      const newColumns = normalizeColumns(res?.data?.orders || []);
      setColumns(newColumns);
    } catch (err) {
      console.error("Polling fetch failed:", err);
    }
  }, [normalizeColumns]);

  // ✅ Match your PHP response shape: { status: "success", orders: [order] }
  const fetchSingleOrder = useCallback(async (orderId) => {
    const res = await axios.get(`https://admin.deedelicious.com/php/get-order.php`, {
      params: { order_id: orderId },
    });

    const order = res?.data?.order || res?.data?.orders?.[0];

    if (!order?.id) {
      throw new Error("Invalid order response from get-order.php");
    }

    return order;
  }, []);

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      setLoading(true);
      await fetchOrders();
      setLoading(false);
    };

    init();

    if (!process.env.REACT_APP_PUSHER_KEY) {
      console.error("❌ Missing Pusher environment variables");
      return () => {};
    }

    const pusher = new Pusher(process.env.REACT_APP_PUSHER_KEY, {
      cluster: process.env.REACT_APP_PUSHER_CLUSTER,
      forceTLS: true,
    });

    const channel = pusher.subscribe("orders-channel");

    channel.bind("new-order", async (data) => {
      if (!isMounted || !data?.order_id) return;

      try {
        const fullOrder = await fetchSingleOrder(data.order_id);

        if (!fullOrder?.id) {
          console.warn("Pusher new-order: missing order payload", data);
          fetchOrders(); // fallback
          return;
        }

        setColumns((prev) => {
          const exists = Object.values(prev)
            .flat()
            .some((o) => o?.id === fullOrder.id);

          if (exists) return prev;

          const updatedTodo = sortOrders([...prev.todo, fullOrder], "todo");

          sounds.current.todo.play().catch((err) => {
            console.warn("Incoming order sound failed:", err);
          });

          return { ...prev, todo: updatedTodo };
        });
      } catch (err) {
        console.error("Failed to fetch new order details:", err);
        fetchOrders(); // fallback
      }
    });

    pusher.connection.bind("state_change", (states) => {
      if (states.current === "disconnected" || states.current === "failed") {
        setPusherError(true);
        if (!pollingInterval.current) {
          pollingInterval.current = setInterval(fetchOrders, 5000);
        }
      }

      if (states.current === "connected") {
        setPusherError(false);
        if (pollingInterval.current) {
          clearInterval(pollingInterval.current);
          pollingInterval.current = null;
        }
      }
    });

    return () => {
      isMounted = false;
      channel.unbind_all();
      channel.unsubscribe();
      pusher.disconnect();

      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
        pollingInterval.current = null;
      }
    };
  }, [fetchOrders, fetchSingleOrder, sortOrders]);

  const toggleOrder = (orderId) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
  };

  const handleDragEnd = async (result) => {
    const { destination, draggableId, source } = result;
    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const orderId = parseInt(draggableId, 10);
    setDragLoading(true);
    const startTime = Date.now();

    // optimistic UI update
    setColumns((prev) => {
      const newColumns = { ...prev };

      const sourceList = Array.from(newColumns[source.droppableId] || []);
      const [movedOrder] = sourceList.splice(source.index, 1);

      const destList = [...(newColumns[destination.droppableId] || []), movedOrder];

      newColumns[source.droppableId] = sourceList;
      newColumns[destination.droppableId] = sortOrders(
        destList,
        destination.droppableId
      );

      return newColumns;
    });

    sounds.current?.[destination.droppableId]?.play?.().catch(() => {});

    try {
      await axios.post(`https://admin.deedelicious.com/php/update-order-status.php`, {
        order_id: orderId,
        status: DB_STATUS_MAP[destination.droppableId],
      });
    } catch (err) {
      console.error("Failed to update order:", err);
      fetchOrders(); // rollback via refresh
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = 1500 - elapsed;
      setTimeout(() => setDragLoading(false), remaining > 0 ? remaining : 0);
    }
  };

  const totalCompletedPrice = useMemo(() => {
    return (columns.completed || []).reduce(
      (sum, order) => sum + (Number(order?.total_price) || 0),
      0
    );
  }, [columns.completed]);

  return (
    <div className="orders-tab">
      {(loading || dragLoading) && (
        <div className="spinner-overlay">
          <div className="spinner"></div>
        </div>
      )}

      {pusherError && (
        <div className="pusher-banner">
          ⚠️ Real-time updates unavailable. Falling back to refresh mode.
        </div>
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="orders-columns">
          {ORDER_STATUSES.map(({ id, label }) => (
            <OrdersColumn
              key={id}
              droppableId={id}
              statusLabel={label}
              orders={columns[id]}
              expandedOrderId={expandedOrderId}
              toggleOrder={toggleOrder}
              refetchOrders={fetchOrders}
              setIsAnyModalActive={setIsAnyModalActive}
              disableDragSort={true}
              footer={
                id === "completed" && (columns.completed || []).length > 0 ? (
                  <div className="completed-total">
                    Total Completed Orders: £{totalCompletedPrice.toFixed(2)}
                  </div>
                ) : null
              }
            />
          ))}
        </div>
      </DragDropContext>
    </div>
  );
}